/**
 * Sign-in through the server-side login proxy (Phase 5, ACC-06 / AUD-04).
 *
 * POST /auth/login and POST /auth/challenge are unauthenticated: the backend
 * calls Cognito, enforces the failed-login protection (a Cloudflare Turnstile
 * CAPTCHA from 3 failures; see TurnstileWidget) and writes the sign-in audit.
 * Both calls use skipAuth, so no Authorization header is sent and a 401 never
 * starts the token-refresh loop in apiClient.
 *
 * SECURITY: never log the request body (it holds the password or MFA code)
 * or the returned tokens.
 */

import { apiClient } from '../api-client';
import { ApiErrorCode, isApiError } from '../../dtos/common/api.dto';

export type AuthChallengeName =
  | 'NEW_PASSWORD_REQUIRED'
  | 'SOFTWARE_TOKEN_MFA'
  | 'MFA_SETUP'
  | 'SELECT_MFA_TYPE';

export interface AuthProxyTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
}

export interface AuthProxyChallenge {
  challengeName: AuthChallengeName;
  session: string;
  challengeParameters?: Record<string, string>;
  username?: string;
}

export type AuthProxyResult = { tokens: AuthProxyTokens } | AuthProxyChallenge;

export interface AuthChallengeRequest {
  username: string;
  challengeName: AuthChallengeName;
  session: string;
  responses: Record<string, string>;
}

export type AuthProxyErrorCode =
  | 'ACCOUNT_LOCKED'
  | 'INVALID_CREDENTIALS'
  | 'CAPTCHA_REQUIRED'
  | 'CAPTCHA_INVALID'
  | 'USER_NOT_CONFIRMED'
  | 'PASSWORD_RESET_REQUIRED'
  | 'INVITE_EXPIRED'
  | 'RATE_LIMITED'
  | 'INVALID_CODE'
  | 'INVALID_PASSWORD'
  | 'SESSION_EXPIRED'
  | 'UNKNOWN';

export const AUTH_MESSAGES: Record<AuthProxyErrorCode, string> = {
  ACCOUNT_LOCKED: 'Too many failed sign-in attempts. Try again in 15 minutes.',
  INVALID_CREDENTIALS: 'Incorrect username or password',
  CAPTCHA_REQUIRED: 'Complete the verification to continue.',
  CAPTCHA_INVALID: 'Verification failed or expired. Try again.',
  USER_NOT_CONFIRMED: 'Your email needs to be verified before signing in. Please check your inbox for a verification code.',
  PASSWORD_RESET_REQUIRED: 'A password reset is required. Use "Forgot password?" to set a new password.',
  INVITE_EXPIRED: 'Your invitation has expired. Ask your administrator to resend it.',
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  INVALID_CODE: 'Invalid MFA code',
  INVALID_PASSWORD: 'Password does not meet the requirements. Please choose a stronger password.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  UNKNOWN: 'Unable to sign in right now. Please try again.',
};

/** A sign-in failure with a stable code and a message that is safe to show. */
export class AuthProxyError extends Error {
  readonly code: AuthProxyErrorCode;
  readonly status?: number;
  /** The next /auth/login attempt needs a Turnstile token (3+ failures for this sign-in name). */
  readonly captchaRequired: boolean;

  constructor(code: AuthProxyErrorCode, status?: number, message?: string, captchaRequired = false) {
    super(message ?? AUTH_MESSAGES[code]);
    this.name = 'AuthProxyError';
    this.code = code;
    this.status = status;
    this.captchaRequired = captchaRequired || code === 'CAPTCHA_REQUIRED' || code === 'CAPTCHA_INVALID';
  }
}

const CHALLENGES: ReadonlySet<string> = new Set([
  'NEW_PASSWORD_REQUIRED',
  'SOFTWARE_TOKEN_MFA',
  'MFA_SETUP',
  'SELECT_MFA_TYPE',
]);

/** Domain codes the challenge route may return for a bad code, password or session. */
const CHALLENGE_CODES: Record<string, AuthProxyErrorCode> = {
  CODE_MISMATCH: 'INVALID_CODE',
  INVALID_CODE: 'INVALID_CODE',
  INVALID_MFA_CODE: 'INVALID_CODE',
  EXPIRED_CODE: 'INVALID_CODE',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  INVALID_SESSION: 'SESSION_EXPIRED',
};

interface RawAuthResponse {
  tokens?: Partial<AuthProxyTokens>;
  challengeName?: string;
  session?: string;
  challengeParameters?: Record<string, string>;
  username?: string;
}

export class AuthProxyService {
  /** Turn any thrown value from /auth/* into an AuthProxyError. */
  static toAuthError(err: unknown, context: 'login' | AuthChallengeName = 'login'): AuthProxyError {
    if (err instanceof AuthProxyError) return err;
    if (!isApiError(err)) return new AuthProxyError('UNKNOWN');

    const code = err.domainCode;
    const status = err.statusCode;
    const captchaRequired = err.details?.captchaRequired === true;

    if (code === 'CAPTCHA_REQUIRED' || code === 'CAPTCHA_INVALID') return new AuthProxyError(code, status);
    // /auth/login answers its own lock with a CAPTCHA; 423 now comes only from /auth/challenge
    // (MFA code lock) or from Cognito's built-in lockout
    if (code === 'ACCOUNT_LOCKED' || status === 423) return new AuthProxyError('ACCOUNT_LOCKED', status);
    if (code === 'USER_NOT_CONFIRMED') return new AuthProxyError('USER_NOT_CONFIRMED', status);
    if (code === 'PASSWORD_RESET_REQUIRED') return new AuthProxyError('PASSWORD_RESET_REQUIRED', status);
    if (code === 'INVITE_EXPIRED') return new AuthProxyError('INVITE_EXPIRED', status);
    if (status === 429 || code === 'RATE_LIMITED' || code === 'TOO_MANY_REQUESTS') {
      return new AuthProxyError('RATE_LIMITED', status);
    }
    if (code && CHALLENGE_CODES[code]) return new AuthProxyError(CHALLENGE_CODES[code], status);
    // A 400 VALIDATION_ERROR (e.g. a malformed username or a non-6-digit code) reads as a bad sign-in / bad code
    if (status === 400 && (code === 'VALIDATION_ERROR' || (!code && err.code === ApiErrorCode.VALIDATION_ERROR))) {
      if (context === 'login') return new AuthProxyError('INVALID_CREDENTIALS', status);
      if (context === 'SOFTWARE_TOKEN_MFA') return new AuthProxyError('INVALID_CODE', status);
    }
    if (code === 'INVALID_CREDENTIALS' || status === 401) {
      if (context === 'login') return new AuthProxyError('INVALID_CREDENTIALS', status, undefined, captchaRequired);
      // On a challenge, a 401 is a wrong MFA code; for the other challenges the session ran out
      return context === 'SOFTWARE_TOKEN_MFA'
        ? new AuthProxyError('INVALID_CODE', status)
        : new AuthProxyError('SESSION_EXPIRED', status);
    }
    if (status === 0) return new AuthProxyError('UNKNOWN', status, 'Network error. Check your connection and try again.');
    return new AuthProxyError('UNKNOWN', status);
  }

  private static toResult(data: RawAuthResponse | undefined, fallbackUsername: string): AuthProxyResult {
    if (data?.tokens?.accessToken && data.tokens.idToken) {
      return {
        tokens: {
          accessToken: data.tokens.accessToken,
          idToken: data.tokens.idToken,
          refreshToken: data.tokens.refreshToken,
          expiresIn: data.tokens.expiresIn,
          tokenType: data.tokens.tokenType,
        },
      };
    }
    if (data?.challengeName && CHALLENGES.has(data.challengeName) && data.session) {
      return {
        challengeName: data.challengeName as AuthChallengeName,
        session: data.session,
        challengeParameters: data.challengeParameters,
        username: data.username || fallbackUsername,
      };
    }
    throw new AuthProxyError('UNKNOWN');
  }

  /**
   * POST /auth/login → tokens or a challenge. Throws AuthProxyError.
   * @param captchaToken Cloudflare Turnstile token; required once the error says captchaRequired.
   *   Tokens are single-use: get a new one for every attempt.
   */
  static async login(username: string, password: string, captchaToken?: string): Promise<AuthProxyResult> {
    try {
      const response = await apiClient.post<RawAuthResponse>(
        '/auth/login',
        captchaToken ? { username, password, captchaToken } : { username, password },
        { skipAuth: true }
      );
      return AuthProxyService.toResult(response.data, username);
    } catch (err) {
      throw AuthProxyService.toAuthError(err, 'login');
    }
  }

  /** POST /auth/challenge → tokens or the next challenge. Throws AuthProxyError. */
  static async respond(request: AuthChallengeRequest): Promise<AuthProxyResult> {
    try {
      const response = await apiClient.post<RawAuthResponse>('/auth/challenge', request, { skipAuth: true });
      return AuthProxyService.toResult(response.data, request.username);
    } catch (err) {
      throw AuthProxyService.toAuthError(err, request.challengeName);
    }
  }
}

/** True when the result carries tokens (sign-in finished). */
export function hasTokens(result: AuthProxyResult): result is { tokens: AuthProxyTokens } {
  return 'tokens' in result;
}

export default AuthProxyService;
